import { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useSubscription } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { FaultCodes } from '@feedflo/fault-codes';

import {
  CREATE_SET_ACTIVE_BIN_COMMAND,
  CREATE_LOCKDOWN_BINS_COMMAND,
  BIN_CHANGE_SUBSCRIPTION,
  LOCKDOWN_FAULT_SUBSCRIPTION,
} from '../../pages/BarnSummaryTab/queries';
import Button, { Colors } from '../../atoms/Button';
import StatusBadge from '../../atoms/StatusBadge';
import FeedFloToolTip from '../../atoms/FeedFloToolTip';
import { ListIcon, LockIcon } from '../../atoms/Icons';
import ConfirmationDialog from '../../organisms/ConfirmationDialog';
import BinControllerFaultHistoryModal from './BinControllerFaultHistoryModal';
import './BinControllerSection.scss';

const LOCKDOWN_EXPLAINER = {
  hard: 'A problem at the bin needs to be fixed before you can switch bins.',
  soft: 'Run the feed line so the sensor can check if feed is back in the system.',
  intentional: 'Bins are held closed by an intentional lockdown. Select a bin below to resume.',
};

const LOCKDOWN_PENDING_EXPLAINER =
  'Waiting for the bin operator to confirm lockdown. No bin is active until lockdown completes.';

/** Intentional lockdown command and UI are not supported on this hardware. */
const BIN_OPERATOR_HW_WITHOUT_INTENTIONAL_LOCKDOWN = 'BC0.1.0';

/** Same priority as BarnSummaryTab activeLockdown derivation. */
function activeLockdownFromFaults(faults) {
  if (!faults?.length) {
    return null;
  }
  if (faults.some((f) => f.code === FaultCodes.BIN_CONTROLLER_INTENTIONAL_LOCK_DOWN)) {
    return 'intentional';
  }
  if (faults.some((f) => f.code === FaultCodes.BIN_CONTROLLER_HARD_LOCK_DOWN)) {
    return 'hard';
  }
  if (faults.some((f) => f.code === FaultCodes.BIN_CONTROLLER_SOFT_LOCK_DOWN)) {
    return 'soft';
  }
  return null;
}

function BinControllerSection({
  deviceId,
  currentBinIndex: initialCurrentBinIndex,
  targetBinIndex: initialTargetBinIndex,
  binControllerBins = [],
  bins = [],
  binSetId = null,
  isMetric = false,
  disabled = false,
  className = '',
  isOnline = true,
  activeLockdown = null,
  deviceHwVersion = null,
}) {
  // --- State hooks ---
  const [currentBinIndex, setCurrentBinIndex] = useState(
    initialCurrentBinIndex !== null && initialCurrentBinIndex !== undefined ? initialCurrentBinIndex : null,
  );
  const [targetBinIndex, setTargetBinIndex] = useState(
    initialTargetBinIndex !== null && initialTargetBinIndex !== undefined ? initialTargetBinIndex : null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isLockdownLoading, setIsLockdownLoading] = useState(false);
  const [hasPendingLockdownCommand, setHasPendingLockdownCommand] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [hasIssuedCommand, setHasIssuedCommand] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [confirmationCallback, setConfirmationCallback] = useState(null);
  const [confirmationConfirmText, setConfirmationConfirmText] = useState('Confirm');
  const [confirmationConfirmColor, setConfirmationConfirmColor] = useState(Colors.Success);

  const pendingTimeoutRef = useRef(null);
  const lockdownPendingTimeoutRef = useRef(null);
  const commandSentAtRef = useRef(0);
  const confirmationDialogRef = useRef(null);

  const supportsIntentionalLockdown = deviceHwVersion !== BIN_OPERATOR_HW_WITHOUT_INTENTIONAL_LOCKDOWN;

  // --- Navigation/side effects ---
  // Update state when props change (e.g., from view refresh)
  useEffect(() => {
    if (initialCurrentBinIndex !== null && initialCurrentBinIndex !== undefined) {
      setCurrentBinIndex(initialCurrentBinIndex);
    }
  }, [initialCurrentBinIndex]);

  useEffect(() => {
    if (initialTargetBinIndex !== null && initialTargetBinIndex !== undefined) {
      setTargetBinIndex(initialTargetBinIndex);
    }
  }, [initialTargetBinIndex]);

  const { data: lockdownSubscriptionData } = useSubscription(LOCKDOWN_FAULT_SUBSCRIPTION, {
    variables: { deviceId },
    skip: !deviceId || disabled,
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
  });

  const effectiveActiveLockdown = useMemo(() => {
    if (lockdownSubscriptionData) {
      return activeLockdownFromFaults(lockdownSubscriptionData.fault);
    }
    return activeLockdown;
  }, [lockdownSubscriptionData, activeLockdown]);

  useEffect(() => {
    if (effectiveActiveLockdown === 'intentional') {
      setHasPendingLockdownCommand(false);
    }
  }, [effectiveActiveLockdown]);

  // --- Derived state ---
  // Determine if there's a pending change: when targetBinIndex doesn't match currentBinIndex
  const hasPendingChange =
    targetBinIndex !== null &&
    targetBinIndex !== undefined &&
    currentBinIndex !== null &&
    currentBinIndex !== undefined &&
    targetBinIndex !== currentBinIndex;

  const [createCommand] = useMutation(CREATE_SET_ACTIVE_BIN_COMMAND, {
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
    onCompleted: (data) => {
      setIsLoading(false);
      // Update targetBinIndex when command is created
      const binIndex = data?.insert_device_command_one?.data?.bin_index;
      if (binIndex !== null && binIndex !== undefined) {
        setTargetBinIndex(binIndex);
      }
    },
    onError: (error) => {
      console.error('Failed to create bin change command:', error);
      Sentry.captureException(error);
      setIsLoading(false);
    },
  });

  const [createLockdownCommand] = useMutation(CREATE_LOCKDOWN_BINS_COMMAND, {
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
    onCompleted: () => {
      setIsLockdownLoading(false);
      setHasPendingLockdownCommand(true);
    },
    onError: (error) => {
      console.error('Failed to create lockdown command:', error);
      Sentry.captureException(error);
      setIsLockdownLoading(false);
      setHasPendingLockdownCommand(false);
    },
  });

  const { data: subscriptionData } = useSubscription(BIN_CHANGE_SUBSCRIPTION, {
    variables: { deviceId },
    skip: !deviceId || !hasPendingChange || disabled,
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
  });

  // --- Navigation/side effects ---
  useEffect(() => {
    if (!subscriptionData?.fault?.[0]) return;
    const fault = subscriptionData.fault[0];
    const sentAt = commandSentAtRef.current;
    const faultStartedAt = fault.started_at;
    const faultTimeMs =
      typeof faultStartedAt === 'string'
        ? new Date(faultStartedAt).getTime()
        : Number(faultStartedAt) > 1e12
          ? Number(faultStartedAt)
          : Number(faultStartedAt) * 1000;
    if (sentAt && faultTimeMs < sentAt) return;

    let faultBinIndex = NaN;
    try {
      const obj = JSON.parse(atob(fault.details)); // base64 -> string -> JSON
      faultBinIndex = obj?.binIndex ?? NaN;
    } catch (e) {
      console.error(e);
    }

    if (!isNaN(faultBinIndex)) {
      setCurrentBinIndex(faultBinIndex);
      setIsLoading(false);
      setHasIssuedCommand(false);
    }
  }, [subscriptionData]);

  // If the device never acknowledges a locally issued command, allow retry after 30 s
  useEffect(() => {
    if (hasPendingChange && hasIssuedCommand) {
      pendingTimeoutRef.current = setTimeout(() => {
        setHasIssuedCommand(false);
      }, 30_000);
    }
    return () => {
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
      }
    };
  }, [hasPendingChange, hasIssuedCommand]);

  useEffect(() => {
    if (hasPendingLockdownCommand && effectiveActiveLockdown !== 'intentional') {
      lockdownPendingTimeoutRef.current = setTimeout(() => {
        setHasPendingLockdownCommand(false);
      }, 30_000);
    }
    return () => {
      if (lockdownPendingTimeoutRef.current) {
        clearTimeout(lockdownPendingTimeoutRef.current);
      }
    };
  }, [hasPendingLockdownCommand, effectiveActiveLockdown]);

  // --- Derived state ---
  const displayBinIndex = currentBinIndex !== null && currentBinIndex !== undefined ? currentBinIndex : null;
  const effectivePendingBinIndex = hasPendingChange ? targetBinIndex : null;
  const isLockdownCommandPending =
    supportsIntentionalLockdown && (isLockdownLoading || hasPendingLockdownCommand);
  const isButtonDisabled =
    disabled || isLoading || (hasPendingChange && hasIssuedCommand) || isLockdownCommandPending;

  // Intentional lockdown (2115) does not clear target_bin_index in bin_controller_state; do not require null.
  const effectiveIntentionalLockdown =
    supportsIntentionalLockdown && effectiveActiveLockdown === 'intentional' && !hasIssuedCommand;

  const suppressActiveBin = effectiveIntentionalLockdown || isLockdownCommandPending;

  // Map bin indices to bin names
  const binMap = useMemo(() => {
    const map = {};
    binControllerBins.forEach((bcb) => {
      if (bcb.index !== null && bcb.index !== undefined && bcb.bin?.name) {
        map[bcb.index] = bcb.bin.name;
      }
    });
    return map;
  }, [binControllerBins]);

  const getBinName = (index) => {
    return binMap[index] || `Bin ${index}`;
  };

  const showConfirmationDialog = (message, onConfirm, options = {}) => {
    setConfirmationMessage(message);
    setConfirmationCallback(() => onConfirm);
    setConfirmationConfirmText(options.confirmLabel ?? 'Confirm');
    setConfirmationConfirmColor(options.confirmColor ?? Colors.Success);
    if (confirmationDialogRef?.current) {
      confirmationDialogRef.current.showModal();
    }
  };

  const resetConfirmationDialog = () => {
    setConfirmationCallback(null);
    setConfirmationMessage('');
    setConfirmationConfirmText('Confirm');
    setConfirmationConfirmColor(Colors.Success);
  };

  const handleConfirmationConfirm = () => {
    if (confirmationCallback) {
      confirmationCallback();
    }
    resetConfirmationDialog();
  };

  const handleConfirmationCancel = () => {
    resetConfirmationDialog();
  };

  const sendSetActiveBinCommand = async (binIndex) => {
    commandSentAtRef.current = Date.now();
    setHasIssuedCommand(true);
    setIsLoading(true);
    const previousTargetBinIndex = targetBinIndex;
    setTargetBinIndex(binIndex);

    try {
      await createCommand({
        variables: {
          deviceId,
          binIndex,
        },
      });
    } catch (error) {
      console.error('Error creating command:', error);
      Sentry.captureException(error);
      setIsLoading(false);
      setTargetBinIndex(previousTargetBinIndex);
    }
  };

  const openBinCommandConfirm = (binIndex) => {
    if (disabled || isLoading || (hasPendingChange && hasIssuedCommand) || !deviceId) {
      return;
    }

    const name = getBinName(binIndex);
    const isVibrateIntent =
      !effectiveIntentionalLockdown && binIndex === displayBinIndex && !hasPendingChange;

    const message = isVibrateIntent ? (
      <div>
        <div className="ConfirmationDialog-messageHeading">
          Vibrate <strong>{name}</strong>
        </div>
        <div className="ConfirmationDialog-messageLine">
          <strong>{name}</strong> will vibrate for ~5 seconds. The active bin won&apos;t change.
        </div>
      </div>
    ) : (
      <div>
        <div className="ConfirmationDialog-messageHeading">
          Switch to <strong>{name}</strong>
        </div>
        <div className="ConfirmationDialog-messageLine">
          <strong>{name}</strong> will vibrate for ~5 seconds and become the active bin.
        </div>
      </div>
    );

    showConfirmationDialog(message, () => {
      sendSetActiveBinCommand(binIndex);
    });
  };

  const openLockdownConfirm = () => {
    if (!supportsIntentionalLockdown || disabled || isLoading || isLockdownCommandPending || !deviceId) {
      return;
    }

    const message = (
      <div>
        <div className="ConfirmationDialog-messageHeading">Lock down bins</div>
        <div className="ConfirmationDialog-messageLine">
          All slides will stay closed until manual intervention.
          <br />
          If a slide is opened by hand it will cose automatically.
          <br />This is rarely needed and should only be used when bins
          must remain shut.
        </div>
      </div>
    );

    showConfirmationDialog(message, () => {
      setIsLockdownLoading(true);
      createLockdownCommand({ variables: { deviceId } }).catch(() => { });
    }, {
      confirmColor: Colors.Danger,
      confirmLabel: 'Yes, lock down bins',
    });
  };

  // --- Function definitions ---
  const handleHistoryClick = () => {
    setIsHistoryModalOpen(true);
  };

  const handleHistoryModalClose = () => {
    setIsHistoryModalOpen(false);
  };

  // --- Render ---
  if (!deviceId) {
    return null;
  }

  const bin0Name = getBinName(0);
  const bin1Name = getBinName(1);
  const isBin0Active = displayBinIndex === 0 && !suppressActiveBin;
  const isBin1Active = displayBinIndex === 1 && !suppressActiveBin;
  const isBin0Pending = effectivePendingBinIndex === 0;
  const isBin1Pending = effectivePendingBinIndex === 1;

  return (
    <div className={`BinControllerSection ${className}`}>
      <ConfirmationDialog
        dialogRef={confirmationDialogRef}
        message={confirmationMessage}
        onCancel={handleConfirmationCancel}
        onConfirm={handleConfirmationConfirm}
        confirmText={confirmationConfirmText}
        confirmColor={confirmationConfirmColor}
      />
      <div className="BinControllerSection-headingRow">
        <div className="BinControllerSection-heading">Bin Controller</div>
        <div className="BinControllerSection-headingActions">
          <StatusBadge status={isOnline ? 'success' : 'offline'} text={isOnline ? 'Online' : 'Offline'} icon="logo" />
          <button
            type="button"
            onClick={handleHistoryClick}
            className="BinControllerSection-historyButton"
            aria-label="View fault history"
          >
            <ListIcon className="BinControllerSection-historyIcon" />
          </button>
        </div>
      </div>
      {effectiveActiveLockdown && effectiveActiveLockdown !== 'intentional' && (
        <div className="BinControllerSection-lockdownRow">
          <FeedFloToolTip description={LOCKDOWN_EXPLAINER[effectiveActiveLockdown]} position="top">
            <span className="BinControllerSection-lockdownBadge">
              <StatusBadge
                status={effectiveActiveLockdown === 'hard' ? 'error' : 'warning'}
                text={
                  effectiveActiveLockdown === 'hard'
                    ? 'Reset Manually (Bins Closed)'
                    : 'Waiting for Auger Run (Bins Closed)'
                }
              />
            </span>
          </FeedFloToolTip>
        </div>
      )}
      {effectiveIntentionalLockdown && (
        <div className="BinControllerSection-lockdownRow">
          <FeedFloToolTip description={LOCKDOWN_EXPLAINER.intentional} position="top">
            <span className="BinControllerSection-lockdownBadge">
              <StatusBadge status="warning" text="Locked Down — Select a bin to resume" />
            </span>
          </FeedFloToolTip>
        </div>
      )}
      <div className="BinControllerSection-content">
        <div className="BinControllerSection-splitButtons">
          <button
            type="button"
            className={`BinControllerSection-splitButton BinControllerSection-splitButton--left ${isBin0Active ? 'BinControllerSection-splitButton--active' : ''
              } ${isBin0Pending ? 'BinControllerSection-splitButton--pending' : ''} ${isButtonDisabled ? 'BinControllerSection-splitButton--disabled' : ''
              }`}
            disabled={isButtonDisabled}
            onClick={() => openBinCommandConfirm(0)}
          >
            <span className="BinControllerSection-splitButton-label">{bin0Name}</span>
            {isBin0Pending && (
              <span className="BinControllerSection-splitButton-indicator BinControllerSection-splitButton-indicator--pending">
                ...
              </span>
            )}
            {isBin0Active && !isBin0Pending && (
              <span className="BinControllerSection-splitButton-indicator BinControllerSection-splitButton-indicator--active">
                ✓
              </span>
            )}
          </button>
          <button
            type="button"
            className={`BinControllerSection-splitButton BinControllerSection-splitButton--right ${isBin1Active ? 'BinControllerSection-splitButton--active' : ''
              } ${isBin1Pending ? 'BinControllerSection-splitButton--pending' : ''} ${isButtonDisabled ? 'BinControllerSection-splitButton--disabled' : ''
              }`}
            disabled={isButtonDisabled}
            onClick={() => openBinCommandConfirm(1)}
          >
            <span className="BinControllerSection-splitButton-label">{bin1Name}</span>
            {isBin1Pending && (
              <span className="BinControllerSection-splitButton-indicator BinControllerSection-splitButton-indicator--pending">
                ...
              </span>
            )}
            {isBin1Active && !isBin1Pending && (
              <span className="BinControllerSection-splitButton-indicator BinControllerSection-splitButton-indicator--active">
                ✓
              </span>
            )}
          </button>
        </div>
        {supportsIntentionalLockdown && !effectiveIntentionalLockdown && (
          isLockdownCommandPending ? (
            <FeedFloToolTip description={LOCKDOWN_PENDING_EXPLAINER} position="top">
              <Button
                variant="text"
                color="secondary"
                left={<span className="BinControllerSection-lockdownSpinner" aria-hidden />}
                content="Lockdown pending..."
                disabled
                className="BinControllerSection-lockdownAction BinControllerSection-lockdownAction--pending"
              />
            </FeedFloToolTip>
          ) : (
            <Button
              variant="text"
              color="secondary"
              left={<LockIcon className="BinControllerSection-lockdownIcon" />}
              content="Lock Down Bins"
              onClick={openLockdownConfirm}
              disabled={disabled || isLoading || !deviceId}
              className="BinControllerSection-lockdownAction"
            />
          )
        )}
      </div>
      <BinControllerFaultHistoryModal
        open={isHistoryModalOpen}
        onClose={handleHistoryModalClose}
        deviceId={deviceId}
        binSetId={binSetId}
        isMetric={isMetric}
        bins={bins}
        binControllerBins={binControllerBins}
      />
    </div>
  );
}

BinControllerSection.propTypes = {
  deviceId: PropTypes.string,
  currentBinIndex: PropTypes.number,
  targetBinIndex: PropTypes.number,
  bins: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      capacityInGrams: PropTypes.number,
    }),
  ),
  binSetId: PropTypes.string,
  isMetric: PropTypes.bool,
  binControllerBins: PropTypes.arrayOf(
    PropTypes.shape({
      index: PropTypes.number,
      bin: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    }),
  ),
  disabled: PropTypes.bool,
  className: PropTypes.string,
  isOnline: PropTypes.bool,
  activeLockdown: PropTypes.oneOf(['hard', 'soft', 'intentional']),
  deviceHwVersion: PropTypes.string,
};

export default BinControllerSection;
